<script setup>
/* global axios */
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useAccount } from 'dashboard/composables/useAccount';

// 👍/👎 nas respostas do BOT (so agentes, so no hover, nunca obrigatorio).
// 👍 = 1 clique e fim. 👎 registra na hora e abre popover OPCIONAL de motivo
// (clicar fora descarta o detalhe, a avaliacao ja valeu). Clicar de novo desfaz.
const props = defineProps({
  messageId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { accountId } = useAccount();

const current = ref(null); // 1 | -1 | null (estado otimista da sessao)
const showReasonPopover = ref(false);
const comment = ref('');
const saving = ref(false);

const REASON_CHIPS = ['WRONG_INFO', 'MISUNDERSTOOD', 'BAD_TONE'];

const endpoint = () =>
  `/api/v1/accounts/${accountId.value}/captain/messages/${props.messageId}/response_rating`;

const submit = async (rating, commentText = null) => {
  saving.value = true;
  try {
    await axios.post(endpoint(), { rating, comment: commentText });
    current.value = rating;
  } catch {
    // silencioso de proposito: avaliacao nunca pode virar fricção
  } finally {
    saving.value = false;
  }
};

const remove = async () => {
  try {
    await axios.delete(endpoint());
    current.value = null;
  } catch {
    // idem
  }
};

const thumbsUp = () => {
  showReasonPopover.value = false;
  if (current.value === 1) {
    remove();
    return;
  }
  submit(1);
};

const thumbsDown = () => {
  if (current.value === -1) {
    showReasonPopover.value = false;
    remove();
    return;
  }
  // registra JA; o popover so enriquece
  submit(-1);
  comment.value = '';
  showReasonPopover.value = true;
};

const sendReason = chipKey => {
  const text = chipKey
    ? t(`CONVERSATION.BOT_RATING.REASON_${chipKey}`)
    : comment.value.trim();
  showReasonPopover.value = false;
  if (!text) return;
  submit(-1, text);
};

const dismissPopover = () => {
  showReasonPopover.value = false;
};
</script>

<template>
  <div class="relative flex items-center gap-0.5 self-end mb-1">
    <button
      class="rating-btn size-6 rounded-md grid place-content-center cursor-pointer transition-colors"
      :class="
        current === 1
          ? 'text-n-teal-9 bg-n-alpha-2'
          : 'text-n-slate-9 hover:text-n-slate-11 hover:bg-n-alpha-2'
      "
      :title="$t('CONVERSATION.BOT_RATING.GOOD')"
      :disabled="saving"
      @click.stop="thumbsUp"
    >
      <span
        class="size-3.5"
        :class="current === 1 ? 'i-lucide-thumbs-up' : 'i-lucide-thumbs-up'"
      />
    </button>
    <button
      class="rating-btn size-6 rounded-md grid place-content-center cursor-pointer transition-colors"
      :class="
        current === -1
          ? 'text-n-ruby-9 bg-n-alpha-2'
          : 'text-n-slate-9 hover:text-n-slate-11 hover:bg-n-alpha-2'
      "
      :title="$t('CONVERSATION.BOT_RATING.BAD')"
      :disabled="saving"
      @click.stop="thumbsDown"
    >
      <span class="size-3.5 i-lucide-thumbs-down" />
    </button>

    <!-- Popover OPCIONAL do 👎 -->
    <div
      v-if="showReasonPopover"
      v-on-click-outside="dismissPopover"
      class="absolute bottom-8 ltr:left-0 rtl:right-0 z-30 w-64 p-2.5 rounded-xl bg-n-solid-1 border border-n-weak shadow-xl flex flex-col gap-2"
    >
      <p class="m-0 text-[11px] font-semibold text-n-slate-11">
        {{ $t('CONVERSATION.BOT_RATING.REASON_TITLE') }}
      </p>
      <div class="flex flex-wrap gap-1">
        <button
          v-for="chip in REASON_CHIPS"
          :key="chip"
          class="px-2 py-0.5 rounded-full text-[10px] font-medium text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer"
          @click.stop="sendReason(chip)"
        >
          {{ $t(`CONVERSATION.BOT_RATING.REASON_${chip}`) }}
        </button>
      </div>
      <input
        v-model="comment"
        type="text"
        class="w-full text-[11px] px-2 py-1.5 rounded-lg bg-n-alpha-1 text-n-slate-12 placeholder-n-slate-9 outline-none"
        :placeholder="$t('CONVERSATION.BOT_RATING.REASON_PLACEHOLDER')"
        @keydown.enter="sendReason(null)"
        @click.stop
      />
    </div>
  </div>
</template>
